import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { CheckStatusType } from '../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { CheckType } from '../../../../../api/types/reports/accountant/check.type';
import { CheckBounceReasonDetailsEntity } from '../../../../../api/types/settings/bouncing-check-reasons/bouncing-check-reasons.type';
import { updateCheckStatusOperation } from '../operations/update-check-status-operation';
import { getCheckBounceReasonsListOperation } from '../../../../settings/services/bouncing-check-reasons/operations/get-check-bounce-reasons-list-operation';
import dateUtils from '../../../../../utils/date-format';

// Backend has no hard max on `limit`; 500 safely covers the realistic count of
// bounce reasons and matches the convention used elsewhere for "fetch all".
const BOUNCE_REASONS_FETCH_LIMIT = 500;

const formatPenalty = (price: number | null | undefined): string => {
  if (typeof price !== 'number') {
    return '—';
  }
  return `₪${Number(price).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const renderBounceReasonOption = (option: CheckBounceReasonDetailsEntity) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      direction: 'rtl',
    }}
  >
    <span>{option.bounceReason}</span>
    <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
      קנס: {formatPenalty(option.paymentServicePrice)}
    </span>
  </div>
);

interface UpdateStatusCheckDialogProps {
  visible: boolean;
  onHide: () => void;
  check: CheckType;
  onStatusUpdated: () => void;
}

export const UpdateStatusCheckDialog: React.FC<
  UpdateStatusCheckDialogProps
> = ({ visible, onHide, check, onStatusUpdated }) => {
  const [selectedStatus, setSelectedStatus] = useState<CheckStatusType>(
    check.checkStatus,
  );
  const [loading, setLoading] = useState(false);

  // Bounce reasons list (loaded once per dialog open).
  const [bounceReasons, setBounceReasons] = useState<
    CheckBounceReasonDetailsEntity[]
  >([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [selectedBounceReasonId, setSelectedBounceReasonId] = useState<
    number | null
  >(null);
  const [valueDate, setValueDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setSelectedStatus(check.checkStatus);
    setSelectedBounceReasonId(null);
    setValueDate(null);

    let cancelled = false;
    const fetchReasons = async () => {
      await getCheckBounceReasonsListOperation(
        0,
        BOUNCE_REASONS_FETCH_LIMIT,
        (data) => {
          if (cancelled) return;
          setBounceReasons(data);
        },
        () => {
          // total not needed for the dropdown
        },
        (l) => {
          if (cancelled) return;
          setReasonsLoading(l);
        },
      );
    };
    fetchReasons();

    return () => {
      cancelled = true;
    };
  }, [visible, check.checkStatus]);

  const statusOptions = [
    { label: 'חזר', value: CheckStatusType.BOUNCED },
    { label: 'בוטל', value: CheckStatusType.CANCELLED },
  ];

  const isBounced = selectedStatus === CheckStatusType.BOUNCED;
  const isMissingBounceReason = isBounced && selectedBounceReasonId === null;
  const isMissingValueDate = isBounced && valueDate === null;

  const handleApprove = async () => {
    if (!selectedStatus) return;
    if (isMissingBounceReason || isMissingValueDate) return;

    try {
      await updateCheckStatusOperation(
        check.paymentId,
        check.checkId,
        selectedStatus,
        isBounced ? selectedBounceReasonId : null,
        isBounced && valueDate
          ? dateUtils.formattedDateWithNoTime(valueDate)
          : null,
        setLoading,
      );
      onStatusUpdated();
    } catch (error) {
      console.error('Error updating check status:', error);
    }
  };

  const isStatusChanged = selectedStatus !== check.checkStatus;
  const isApproveDisabled =
    !isStatusChanged || isMissingBounceReason || isMissingValueDate || loading;

  const approveDisabledTooltip = isMissingBounceReason
    ? 'יש לבחור סיבת חזרה'
    : isMissingValueDate
      ? 'יש לבחור תאריך ערך'
      : undefined;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="עדכון סטטוס המחאה"
      modal
      style={{ width: '500px' }}
      footer={
        <div
          style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}
        >
          <Button label="ביטול" outlined onClick={onHide} disabled={loading} />
          <Button
            label="אישור"
            loading={loading}
            disabled={isApproveDisabled}
            tooltip={approveDisabledTooltip}
            tooltipOptions={{ showOnDisabled: true, position: 'top' }}
            onClick={handleApprove}
          />
        </div>
      }
    >
      <div style={{ padding: '20px 0' }}>
        <div style={{ marginBottom: '20px', textAlign: 'right' }}>
          <p style={{ fontSize: '16px', color: '#333' }}>
            יש לבחור את סטטוס ההמחאה המבוקשת
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label
            htmlFor="checkStatus"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              textAlign: 'right',
            }}
          >
            סטטוס המחאה:
          </label>
          <Dropdown
            id="checkStatus"
            value={selectedStatus}
            options={statusOptions}
            onChange={(e) => {
              setSelectedStatus(e.value);
              // Clear bounce-only fields when leaving BOUNCED so we never
              // accidentally send them for CANCELLED (backend forbids valueDate).
              if (e.value !== CheckStatusType.BOUNCED) {
                setSelectedBounceReasonId(null);
                setValueDate(null);
              }
            }}
            placeholder="בחר סטטוס"
            style={{ width: '100%', textAlign: 'right' }}
            disabled={loading}
          />
        </div>

        {isBounced && (
          <>
            <div style={{ marginBottom: '20px' }}>
              <label
                htmlFor="bounceReason"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}
              >
                סיבת חזרה:
              </label>
              <Dropdown
                id="bounceReason"
                value={selectedBounceReasonId}
                options={bounceReasons}
                optionLabel="bounceReason"
                optionValue="id"
                onChange={(e) => setSelectedBounceReasonId(e.value)}
                placeholder={
                  reasonsLoading
                    ? 'טוען סיבות חזרה...'
                    : bounceReasons.length === 0
                      ? 'לא הוגדרו סיבות חזרה'
                      : 'בחר סיבת חזרה'
                }
                disabled={
                  loading || reasonsLoading || bounceReasons.length === 0
                }
                filter
                filterBy="bounceReason"
                emptyMessage="לא הוגדרו סיבות חזרה"
                emptyFilterMessage="לא נמצאו תוצאות"
                style={{ width: '100%', textAlign: 'right' }}
                itemTemplate={renderBounceReasonOption}
                valueTemplate={(_value, props) => {
                  const selected = bounceReasons.find(
                    (r) => r.id === selectedBounceReasonId,
                  );
                  if (!selected) {
                    return (
                      <span style={{ color: '#6b7280' }}>
                        {props.placeholder}
                      </span>
                    );
                  }
                  return renderBounceReasonOption(selected);
                }}
              />
              {!reasonsLoading && bounceReasons.length === 0 && (
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '12px',
                    color: '#991B1B',
                    textAlign: 'right',
                  }}
                >
                  יש להגדיר סיבות חזרה בהגדרות לפני סימון המחאה כנדחתה.
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label
                htmlFor="valueDate"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  textAlign: 'right',
                }}
              >
                תאריך ערך:
              </label>
              <Calendar
                id="valueDate"
                value={valueDate}
                onChange={(e) => setValueDate((e.value as Date) || null)}
                dateFormat="dd/mm/yy"
                showIcon
                iconPos="right"
                placeholder="בחר תאריך ערך"
                disabled={loading}
                style={{
                  width: '100%',
                  direction: 'ltr',
                }}
                inputStyle={{ direction: 'rtl', width: '100%' }}
              />
            </div>
          </>
        )}

        <div
          style={{
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            textAlign: 'right',
          }}
        >
          <div style={{ marginBottom: '8px' }}>
            <strong>פרטי המחאה:</strong>
          </div>
          <div>מספר המחאה: {check.checkNumber}</div>
          <div>בנק: {check.bankNumber}</div>
          <div>סניף: {check.branchNumber}</div>
          <div>חשבון: {check.accountNumber}</div>
          <div>סכום: ₪{check.paymentAmount}</div>
        </div>
      </div>
    </Dialog>
  );
};
